<script setup lang="ts">
import { useHorizontalScroll } from './useHorizontalScroll'

const props = defineProps<{
  as?: 'ul' | 'div'
  scrollPercentage?: number
  containerClass?: HTMLElement['className']
}>()

const { carouselRef, fadePosition, handleScroll, scrollDirection }
  = useHorizontalScroll(props.scrollPercentage)
const mutationObserver = ref<MutationObserver | null>(null)
const resizeObserver = ref<ResizeObserver | null>(null)

onMounted(() => {
  const el = carouselRef.value
  if (!el)
    return

  handleScroll()
  mutationObserver.value = new MutationObserver(() => {
    handleScroll()
  })
  mutationObserver.value.observe(el, { childList: true, subtree: true })

  resizeObserver.value = new ResizeObserver(() => {
    handleScroll()
  })
  resizeObserver.value.observe(el)
})

onBeforeUnmount(() => {
  mutationObserver.value?.disconnect()
  resizeObserver.value?.disconnect()
})
</script>

<template>
  <div class="relative w-full overflow-x-auto group">
    <button
      v-if="fadePosition === 'left' || fadePosition === 'both'"
      class="absolute top-1/2 left-0 -translate-y-1/2 z-10 h-6 w-6 hidden md:block opacity-0 group-hover:opacity-100 transition-opacity"
      i-mdi:chevron-left
      @click="scrollDirection('left')"
    />

    <button
      v-if="fadePosition === 'right' || fadePosition === 'both'"
      class="absolute top-1/2 right-0 -translate-y-1/2 z-10 h-6 w-6 hidden md:block opacity-0 group-hover:opacity-100 transition-opacity"
      i-mdi:chevron-right
      @click="scrollDirection('right')"
    />

    <component
      :is="props.as ?? 'div'"
      ref="carouselRef"
      class="flex overflow-x-auto no-scrollbar space-x-2 items-center w-full"
      :class="[{
        fadeOnLeft: fadePosition === 'left',
        fadeOnRight: fadePosition === 'right',
        fadeHorizontally: fadePosition === 'both',
      }, containerClass]"
      @scroll="handleScroll"
    >
      <slot />
    </component>
  </div>
</template>
